/**
 * Connect Page - Pre-auth landing page for Shopify OAuth installation
 *
 * This page is accessible without authentication and allows merchants to:
 * - Enter their shop domain to initiate OAuth
 * - See required permissions/scopes
 * - Trust badges and verification
 */

import { useState, useEffect } from 'react';
import { oauthApi } from '../services/api';
import { getShopFromUrl } from '../lib/shopify-app-bridge';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Chip,
  ChipGroup,
  Field,
  FlowDivider,
  FlowFine,
  FlowIntro,
  FlowList,
  FlowScreen,
  Inline,
  Stack,
} from '../ds';

interface ConnectPageProps {
  onBack?: () => void;
}

const SHOPIFY_SCOPES = [
  { name: 'Read & Write Products', description: 'View and sync your product catalog' },
  { name: 'Read & Write Customers', description: 'Access customer data for personalization' },
  { name: 'Read & Write Orders', description: 'Track orders and conversions' },
  { name: 'Read & Write Checkouts', description: 'Provide checkout assistance' },
  { name: 'Read & Write Inventory', description: 'Real-time stock information' },
];

export default function ConnectPage({ onBack }: ConnectPageProps) {
  const [shop, setShop] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill shop from URL parameter if present
  useEffect(() => {
    const shopFromUrl = getShopFromUrl();
    if (shopFromUrl) {
      setShop(shopFromUrl.replace('.myshopify.com', ''));
    }
  }, []);

  const handleConnect = async () => {
    if (!shop.trim()) {
      setError('Please enter your store name');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Normalize shop domain
      const shopDomain = shop.includes('.myshopify.com') ? shop : `${shop}.myshopify.com`;

      // Get OAuth installation URL
      const response = await oauthApi.getInstallUrl(shopDomain);

      if (response.success && response.data?.authorizationUrl) {
        // Redirect to Shopify OAuth
        window.location.href = response.data.authorizationUrl;
      } else {
        setError(response.error || 'Failed to initiate connection');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' && !isLoading) {
      handleConnect();
    }
  };

  const check = <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7" /></svg>;

  return (
    <FlowScreen>
      <FlowIntro title="AI-Powered E-commerce Engagement">
        Customers reply to emails &amp; SMS, AI responds automatically
      </FlowIntro>

      <Card padding="lg">
        <CardHeader
          title="Connect Your Shopify Store"
          titleAs="h2"
          description="Enter your store name to get started with one-click installation"
        />
        <Stack>
          <Field
            id="shop"
            label="Store Name"
            suffix=".myshopify.com"
            value={shop}
            onChange={(e) => setShop((e.target as HTMLInputElement).value)}
            onKeyDown={handleKeyPress}
            placeholder="your-store"
            disabled={isLoading}
          />

          {error && <Alert tone="danger" live="assertive">{error}</Alert>}

          <Button block size="lg" onClick={handleConnect} busy={isLoading} busyLabel="Connecting...">
            Connect with Shopify
          </Button>

          <FlowDivider>Required Permissions</FlowDivider>

          <FlowList
            variant="check"
            label="Required permissions"
            items={SHOPIFY_SCOPES.map((scope) => ({
              key: scope.name,
              icon: check,
              title: scope.name,
              description: scope.description,
            }))}
          />
        </Stack>
      </Card>

      <ChipGroup label="Trust">
        <Chip icon={<svg viewBox="0 0 24 24"><path d="M5 11h14v9H5zM8 11V8a4 4 0 0 1 8 0v3" /></svg>}>SSL Secured</Chip>
        <Chip icon={<svg viewBox="0 0 24 24"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6zM9 12l2 2 4-4" /></svg>}>GDPR Compliant</Chip>
        <Chip icon={<svg viewBox="0 0 24 24"><path d="M3 10h18M7 15h1m4 0h1M6 5h12a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3z" /></svg>}>Shopify Partner</Chip>
      </ChipGroup>

      {onBack && (
        <Inline justify="end">
          <Button variant="ghost" onClick={onBack}>Already have an account? Sign in</Button>
        </Inline>
      )}

      <FlowFine>
        By connecting, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>
      </FlowFine>
    </FlowScreen>
  );
}
